import { useQuery } from '@tanstack/react-query'
import type { OpportunityListQuery, OpportunityListResponse } from 'backend/types'
import { useMemo } from 'react'
import { getOpportunities } from '@/lib'
import { queryKeys } from '@/lib/queryKeys'
import { opportunityTabs } from '../-components/tabs'

// オポチュニティリストアイテムの型をエクスポート
export type OpportunityListItem = OpportunityListResponse['opportunities'][number]

interface UseOpportunitiesOptions extends Partial<OpportunityListQuery> {
  enabled?: boolean // データ取得を有効化するか（デフォルト: true）
  /** タブキー（指定した場合は自動的に queryParams を計算） */
  tabKey?: string
}

/**
 * オポチュニティ一覧を取得するカスタムフック
 *
 * tabKey を指定すると、タブの定義に基づいて自動的に queryParams を計算します。
 * それ以外の場合は all, salesforceProjectMemberTypeId などを直接指定できます。
 */
export function useOpportunities(options: UseOpportunitiesOptions = {}) {
  const { all, salesforceProjectMemberTypeId, offset, limit, enabled = true, tabKey } = options

  // タブキーが指定されている場合は、タブの定義から queryParams を自動計算
  const queryParams = useMemo(() => {
    if (!tabKey) {
      return { all, salesforceProjectMemberTypeId, offset, limit }
    }

    const currentTab = opportunityTabs.find((tab) => tab.key === tabKey)
    if (!currentTab) {
      return { all, salesforceProjectMemberTypeId, offset, limit }
    }

    if (currentTab.isAll) {
      return { all: true, offset, limit }
    }

    if (currentTab.salesforceProjectMemberTypeId) {
      return {
        salesforceProjectMemberTypeId: currentTab.salesforceProjectMemberTypeId,
        offset,
        limit,
      }
    }

    // owner: パラメータなし（自分が起票したもの）
    return { offset, limit }
  }, [tabKey, all, salesforceProjectMemberTypeId, offset, limit])

  return useQuery({
    queryKey: queryKeys.opportunities.list(queryParams),
    queryFn: async () => {
      return getOpportunities(queryParams)
    },
    enabled,
  })
}
